import { useMemo } from 'react';
import { Moment } from 'moment-timezone';
import { FRIDGE_WARNING_F } from './hvacStatus';
import { useEsensorChartRaw } from './useSensorSparkline';

type RawRecord = { Timestamp: string; jInfo: string };

export type FridgePoint = { time: number; temp: number };

export interface FridgeStats {
  count: number;
  min: number | null;
  max: number | null;
  spikes: number;
  minRecoverySec: number | null;
  maxRecoverySec: number | null;
  points: FridgePoint[];
}

export const formatDuration = (seconds: number | null): string => {
  if (seconds === null) return '—';
  if (seconds < 60) return `${Math.max(1, Math.round(seconds))}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
  const h = Math.floor(seconds / 3600);
  const m = Math.round((seconds % 3600) / 60);
  return m ? `${h}h ${m}m` : `${h}h`;
};

export const computeFridgeStats = (
  points: FridgePoint[],
  nominalMax: number = FRIDGE_WARNING_F,
): FridgeStats => {
  if (points.length === 0) {
    return {
      count: 0,
      min: null,
      max: null,
      spikes: 0,
      minRecoverySec: null,
      maxRecoverySec: null,
      points: [],
    };
  }

  let min = Infinity;
  let max = -Infinity;
  let inSpike = false;
  let spikeCount = 0;
  let peakTime = 0;
  let peakTemp = -Infinity;
  const recoveries: number[] = [];

  for (const p of points) {
    if (p.temp < min) min = p.temp;
    if (p.temp > max) max = p.temp;

    if (p.temp > nominalMax) {
      if (!inSpike) {
        inSpike = true;
        spikeCount += 1;
        peakTime = p.time;
        peakTemp = p.temp;
      } else if (p.temp > peakTemp) {
        peakTemp = p.temp;
        peakTime = p.time;
      }
    } else if (inSpike) {
      recoveries.push((p.time - peakTime) / 1000);
      inSpike = false;
      peakTemp = -Infinity;
    }
  }

  return {
    count: points.length,
    min,
    max,
    spikes: spikeCount,
    minRecoverySec: recoveries.length ? Math.min(...recoveries) : null,
    maxRecoverySec: recoveries.length ? Math.max(...recoveries) : null,
    points,
  };
};

const withLiveReading = (
  points: FridgePoint[],
  tempF: number | null | undefined,
  startMs: number,
  endMs: number,
): FridgePoint[] => {
  if (typeof tempF !== 'number' || Number.isNaN(tempF)) return points;
  if (!(endMs > startMs)) return points;
  const time = Math.min(Math.max(Date.now(), startMs), endMs);
  const last = points[points.length - 1];
  if (last && time <= last.time) return points;
  return [...points, { time, temp: tempF }];
};

const parsePoints = (data: RawRecord[] | undefined): FridgePoint[] => {
  if (!data) return [];
  const points: FridgePoint[] = [];
  data.forEach((row) => {
    try {
      const parsed = JSON.parse(row.jInfo || '{}') as { TempF?: number };
      if (typeof parsed.TempF === 'number') {
        points.push({ time: new Date(row.Timestamp).getTime(), temp: parsed.TempF });
      }
    } catch {
      // ignore malformed rows
    }
  });
  points.sort((a, b) => a.time - b.time);
  return points;
};

export const useFridgeTempHistory = (
  deviceEUI: string,
  startDateTime: Moment,
  endDateTime: Moment,
  nominalMax: number = FRIDGE_WARNING_F,
  liveTempF?: number | null,
) => {
  const startMs = startDateTime.valueOf();
  const endMs = endDateTime.valueOf();
  const query = useEsensorChartRaw(deviceEUI, startDateTime, endDateTime);

  const stats = useMemo<FridgeStats | null>(() => {
    if (!query.data) return null;
    const points = withLiveReading(parsePoints(query.data), liveTempF, startMs, endMs);
    return computeFridgeStats(points, nominalMax);
  }, [query.data, nominalMax, liveTempF, startMs, endMs]);

  return {
    ...query,
    stats,
    points: stats?.points ?? [],
  };
};
